import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { usePageSeo, breadcrumbJsonLd } from "../lib/seo";
import { restaurantJsonLd, NAP, CANON } from "../data/nap";
import content from "../data/seo_content.json";
import { NapBlock } from "../components/NapBlock";
import { FactsBlock } from "../components/FactsBlock";
import { LegacyRedirect, resolveLegacyPath } from "../components/LegacyRedirect";
import NotFound from "./NotFound";

const T = content.tagPages;
const MEAT = new RegExp(T.meatWords, "i");
const DAIRY = new RegExp(T.dairyWords, "i");

export function tagMatches(tag, products) {
  const m = tag.match;
  return products.filter((p) => {
    const n = p.name || "";
    if (m.any && !m.any.some((k) => n.toLowerCase().includes(k.toLowerCase()))) return false;
    if (m.veg && MEAT.test(n)) return false;
    if (m.vegan && (MEAT.test(n) || DAIRY.test(n))) return false;
    if (m.meat && !MEAT.test(n)) return false;
    if ((m.exclude || []).some((x) => n.toLowerCase().includes(x))) return false;
    return true;
  });
}

const price = (p) => Number(p.display_price ?? p.base_price ?? 0).toFixed(2);

// /tags/{dish}: a real, indexable page — matching restaurant dishes with prices + Menu JSON-LD. Unknown tags keep their legacy redirect.
export default function TagPage() {
  const { slug } = useParams();
  const tag = T.tags.find((t) => t.slug === slug);
  const [products, setProducts] = useState([]);
  useEffect(() => {
    if (!tag) return;
    api.get("/businesses/biz_restaurant/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, [tag]);
  const items = useMemo(() => (tag ? tagMatches(tag, products) : []), [tag, products]);
  const dish = tag?.dish || "";
  usePageSeo({
    title: tag ? T.titleTemplate.replace("{DISH}", dish) : undefined,
    description: tag ? T.descriptionTemplate.replace("{DISH}", dish) : undefined,
    canonicalPath: tag ? (tag.canonical || `/tags/${tag.slug}`) : undefined,
    jsonLd: tag ? [
      { "@context": "https://schema.org", "@type": "Menu", "@id": `${CANON}/tags/${tag.slug}#menu`, name: `${dish} — ${NAP.name}`, url: `${CANON}/tags/${tag.slug}`, inLanguage: "en-US",
        hasMenuSection: [{ "@type": "MenuSection", name: dish, hasMenuItem: items.map((p) => ({ "@type": "MenuItem", name: p.name, url: `${CANON}/menu/${p.slug}`,
          offers: { "@type": "Offer", price: price(p), priceCurrency: "USD", url: `${CANON}/store/restaurant` } })) }] },
      restaurantJsonLd({ "@id": `${CANON}/tags/${tag.slug}#restaurant` }),
      breadcrumbJsonLd([["Home", "/"], [dish, `/tags/${tag.slug}`]]),
    ] : undefined,
  });
  if (!tag) return resolveLegacyPath(`/tags/${slug}`) ? <LegacyRedirect /> : <NotFound robots="noindex, follow" canonicalPath="/store/restaurant" />;
  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 pb-20" data-testid={`tag-page-${tag.slug}`}>
      <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#db594b]">{NAP.name}</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-2" data-testid="tag-h1">{T.h1Template.replace("{DISH}", dish)}</h1>
      <p className="text-base mt-4 leading-relaxed max-w-2xl" data-testid="tag-paragraph">{tag.paragraph}</p>
      <Link to="/store/restaurant" data-testid="tag-order-online" className="inline-flex mt-5 px-6 py-3 rounded-full bg-black text-white font-bold min-h-[44px] items-center">Order Online</Link>
      <h2 className="text-lg font-bold mt-10">{dish} on our menu</h2>
      <ul className="mt-3 divide-y divide-neutral-200" data-testid="tag-items">
        {items.map((p) => (
          <li key={p.id} className="flex items-center justify-between py-3 gap-4" data-testid={`tag-item-${p.slug}`}>
            <Link to={`/menu/${p.slug}`} className="font-semibold hover:underline">{p.name}</Link>
            <span className="font-bold tabular-nums">${price(p)}</span>
          </li>
        ))}
        {!items.length && <li className="py-3 text-neutral-500 text-sm" data-testid="tag-items-empty">Loading dishes…</li>}
      </ul>
      <section className="mt-10 max-w-3xl"><FactsBlock /></section>
      <NapBlock />
    </main>
  );
}
